/* Distopion product-specific size recommender.
   Garment measurements are the source of truth. A product's primary body
   measurement (waist for bottoms, chest for tops) is always required. */

(() => {
  'use strict';

  const VERSION = 3;
  const STORAGE_KEY = 'distopion:size-profile:v3';
  const LEGACY_STORAGE_KEY = 'distopion:size-profile:v2';
  const CM_PER_INCH = 2.54;
  const KG_PER_POUND = 0.45359237;

  const BOTTOM_WAIST_EASE = {
    none: { closer: 1, intended: 2.5, looser: 4.5 },
    low: { closer: 0, intended: 1.5, looser: 3.5 },
    medium: { closer: -1, intended: 0.5, looser: 2.5 },
    high: { closer: -2, intended: -0.5, looser: 1.5 },
  };

  const BOTTOM_HIP_EASE = {
    fitted: 2,
    regular: 4,
    relaxed: 8,
    oversized: 12,
  };

  const HIP_FIT_ADJUSTMENT = {
    closer: -2,
    intended: 0,
    looser: 3,
  };

  const TOP_CHEST_ROOM = {
    fitted: [2, 8],
    regular: [7, 15],
    relaxed: [12, 24],
    oversized: [18, 36],
  };

  const TOP_FIT_SHIFT = {
    closer: -4,
    intended: 0,
    looser: 4,
  };

  const MEASUREMENT_KEYS = [
    'waistCm',
    'waistStretchedCm',
    'hipCm',
    'thighCm',
    'legOpeningCm',
    'outseamCm',
    'inseamCm',
    'riseCm',
    'chestCm',
    'shoulderCm',
    'garmentLengthCm',
    'sleeveCm',
  ];

  const finiteNumber = (value) => {
    if (value === null || value === undefined) return null;
    if (typeof value === 'string' && value.trim() === '') return null;
    const number = Number(value);
    return Number.isFinite(number) ? number : null;
  };

  const clamp = (value, minimum, maximum) => Math.min(maximum, Math.max(minimum, value));

  const round = (value, precision = 1) => {
    const factor = 10 ** precision;
    return Math.round(value * factor) / factor;
  };

  const titleCase = (value) => {
    const text = String(value || '').replace(/[_-]+/g, ' ');
    return text ? text.charAt(0).toUpperCase() + text.slice(1) : '';
  };

  const normalizeLabel = (label) =>
    String(label ?? '')
      .trim()
      .toUpperCase()
      .replace(/\s+/g, '')
      .replace(/^X{3}L$/, '3XL')
      .replace(/^X{2}L$/, 'XXL');

  const normalizeNumberArray = (value) => {
    if (!Array.isArray(value)) return [];
    return value.map((item) => finiteNumber(item));
  };

  function validateConfig(rawConfig) {
    const raw = rawConfig && typeof rawConfig === 'object' ? rawConfig : {};
    const errors = [];
    const sizeLabels = Array.isArray(raw.sizeLabels)
      ? raw.sizeLabels.map((label) => String(label ?? '').trim()).filter(Boolean)
      : [];
    const profileType = String(raw.profileType || '').toLowerCase();
    const intendedFit = String(raw.intendedFit || 'regular').toLowerCase();
    const stretchLevel = String(raw.stretchLevel || 'none').toLowerCase();
    const measurementBasis = String(raw.measurementBasis || '').toLowerCase();
    const toleranceCm = finiteNumber(raw.toleranceCm) ?? 1.5;
    const measurements = {};
    const optionValues = Array.isArray(raw.optionValues)
      ? raw.optionValues.map((option) => ({
          id: option?.id,
          label: String(option?.label ?? '').trim(),
          available: option?.available !== false,
        }))
      : [];
    const suppliedSupplierLabels = Array.isArray(raw.supplierSizeLabels)
      ? raw.supplierSizeLabels.map((label) => String(label ?? '').trim())
      : [];

    if (sizeLabels.length < 2) errors.push('At least two customer-facing sizes are required.');
    if (!['bottom', 'top'].includes(profileType)) errors.push('Profile type must be bottom or top.');
    if (!['fitted', 'regular', 'relaxed', 'oversized'].includes(intendedFit)) errors.push('Intended fit is not supported.');
    if (!Object.prototype.hasOwnProperty.call(BOTTOM_WAIST_EASE, stretchLevel)) errors.push('Stretch level is not supported.');
    if (!['garment', 'garment_circumference'].includes(measurementBasis)) {
      errors.push('Measurements must be stored as full garment circumferences in centimetres.');
    }
    if (optionValues.length !== sizeLabels.length) errors.push('Shopify size options do not align with the customer-facing size list.');
    if (optionValues.some((option) => option.id === null || option.id === undefined || !option.label)) {
      errors.push('Every Shopify size option must have an ID and label.');
    }
    if (suppliedSupplierLabels.length && suppliedSupplierLabels.length !== sizeLabels.length) {
      errors.push('Supplier size labels do not align with customer-facing size labels.');
    }

    MEASUREMENT_KEYS.forEach((key) => {
      measurements[key] = normalizeNumberArray(raw[key]);
      if (measurements[key].length && measurements[key].length !== sizeLabels.length) {
        errors.push(`${key} does not align with the size label list.`);
      }
    });

    const requiredKeys = profileType === 'bottom' ? ['waistCm', 'hipCm'] : ['chestCm'];
    requiredKeys.forEach((key) => {
      const values = measurements[key];
      if (values.length !== sizeLabels.length || values.some((value) => !Number.isFinite(value) || value <= 0)) {
        errors.push(`${key} is required for every size.`);
      }
    });

    if (profileType === 'bottom' && Boolean(raw.elasticWaist)) {
      const stretched = measurements.waistStretchedCm;
      if (stretched.length !== sizeLabels.length || stretched.some((value) => !Number.isFinite(value) || value <= 0)) {
        errors.push('waistStretchedCm is required for every elastic-waist size.');
      } else if (measurements.waistCm.length === sizeLabels.length) {
        stretched.forEach((value, index) => {
          if (value + toleranceCm < measurements.waistCm[index]) {
            errors.push('A stretched waistband cannot be smaller than its relaxed waistband.');
          }
        });
      }
    }

    Object.entries(measurements).forEach(([key, values]) => {
      if (!values.length) return;
      if (values.some((value) => value !== null && (!Number.isFinite(value) || value <= 0))) {
        errors.push(`${key} contains an invalid value.`);
        return;
      }
      for (let index = 1; index < values.length; index += 1) {
        if (values[index] !== null && values[index - 1] !== null && values[index] < values[index - 1] - toleranceCm) {
          errors.push(`${key} decreases between consecutive sizes.`);
          break;
        }
      }
    });

    const supplierSizeLabels = sizeLabels.map(
      (label, index) => suppliedSupplierLabels[index] || optionValues[index]?.label || label
    );
    const sizes = sizeLabels.map((label, index) => {
      const row = { label, supplierLabel: supplierSizeLabels[index], optionValue: optionValues[index] || null, index };
      MEASUREMENT_KEYS.forEach((key) => { row[key] = measurements[key][index] ?? null; });
      return row;
    });

    return {
      valid: errors.length === 0,
      errors: [...new Set(errors)],
      config: {
        version: finiteNumber(raw.version) ?? VERSION,
        productHandle: String(raw.productHandle || ''),
        sectionId: String(raw.sectionId || ''),
        optionPosition: finiteNumber(raw.optionPosition),
        optionValues,
        sizeLabels,
        supplierSizeLabels,
        profileType,
        intendedFit,
        stretchLevel,
        elasticWaist: Boolean(raw.elasticWaist),
        measurementBasis,
        toleranceCm: clamp(toleranceCm, 0, 5),
        sizes,
      },
    };
  }

  const estimateBodyMeasurements = (input) => {
    const heightCm = finiteNumber(input.heightCm);
    const weightKg = finiteNumber(input.weightKg);
    const stomachShape = String(input.stomachShape || '');
    const torsoShape = String(input.torsoShape || '');
    const thighShape = String(input.thighShape || '');
    if (
      !Number.isFinite(heightCm) ||
      !Number.isFinite(weightKg) ||
      !['flat', 'average', 'round'].includes(stomachShape) ||
      !['slim', 'average', 'broad'].includes(torsoShape) ||
      !['slim', 'average', 'full'].includes(thighShape)
    ) return null;

    const bmi = clamp(weightKg / ((heightCm / 100) ** 2), 15, 45);
    const age = finiteNumber(input.age);
    const ageRatio = Number.isFinite(age) ? clamp((age - 30) * 0.00025, -0.004, 0.015) : 0;
    const stomachRatio = { flat: -0.035, average: 0, round: 0.045 }[stomachShape];
    const torsoAdjustment = { slim: -5, average: 0, broad: 7 }[torsoShape];
    const thighAdjustment = { slim: -3, average: 0, full: 5 }[thighShape];

    const waistRatio = clamp(0.45 + (bmi - 22) * 0.0115 + ageRatio + stomachRatio, 0.36, 0.75);
    const waistCm = heightCm * waistRatio;
    const hipCm = waistCm + 19 + thighAdjustment + (bmi - 22) * 0.35;
    const chestCm = heightCm * 0.52 + (bmi - 22) * 1.8 + torsoAdjustment;
    const shoulderCm = heightCm * 0.235 + ({ slim: -2, average: 0, broad: 3 }[torsoShape]);

    return {
      bmi,
      waistCm: round(waistCm, 1),
      hipCm: round(hipCm, 1),
      chestCm: round(chestCm, 1),
      shoulderCm: round(shoulderCm, 1),
    };
  };

  function buildBodyProfile(input, config) {
    const estimated = estimateBodyMeasurements(input);
    const body = {
      heightCm: finiteNumber(input.heightCm),
      weightKg: finiteNumber(input.weightKg),
      age: finiteNumber(input.age),
      stomachShape: input.stomachShape || '',
      torsoShape: input.torsoShape || '',
      thighShape: input.thighShape || '',
      fitPreference: input.fitPreference || 'intended',
      sources: {},
      estimated,
    };

    if (config.profileType === 'bottom') {
      const exactWaist = finiteNumber(input.bodyWaistCm);
      const exactHip = finiteNumber(input.bodyHipCm);
      body.waistCm = exactWaist ?? estimated?.waistCm ?? null;
      body.hipCm = exactHip ?? estimated?.hipCm ?? null;
      body.sources.waist = exactWaist !== null ? 'exact' : body.waistCm !== null ? 'estimated' : 'missing';
      body.sources.hip = exactHip !== null ? 'exact' : body.hipCm !== null ? 'estimated' : 'missing';
    } else {
      const exactChest = finiteNumber(input.bodyChestCm);
      const exactShoulder = finiteNumber(input.bodyShoulderCm);
      body.chestCm = exactChest ?? estimated?.chestCm ?? null;
      body.shoulderCm = exactShoulder ?? estimated?.shoulderCm ?? null;
      body.sources.chest = exactChest !== null ? 'exact' : body.chestCm !== null ? 'estimated' : 'missing';
      body.sources.shoulder = exactShoulder !== null ? 'exact' : body.shoulderCm !== null ? 'estimated' : 'missing';
    }
    return body;
  }

  function scoreBottomSize(size, body, config) {
    const tolerance = config.toleranceCm;
    const preference = body.fitPreference;
    const waistEase = BOTTOM_WAIST_EASE[config.stretchLevel]?.[preference] ?? BOTTOM_WAIST_EASE.none.intended;
    const targetWaist = body.waistCm + waistEase;
    const hipEase = Math.max(0, (BOTTOM_HIP_EASE[config.intendedFit] ?? BOTTOM_HIP_EASE.regular) + (HIP_FIT_ADJUSTMENT[preference] ?? 0));
    const targetHip = body.hipCm + hipEase;
    const waistMaximum = config.elasticWaist ? size.waistStretchedCm : size.waistCm;
    const waistDeficit = Math.max(0, targetWaist - waistMaximum - tolerance);
    const hipDeficit = Math.max(0, targetHip - size.hipCm - tolerance);
    const waistRoom = waistMaximum - body.waistCm;
    const hipRoom = size.hipCm - body.hipCm;

    let waistDistance;
    if (config.elasticWaist) {
      const relaxed = size.waistCm;
      const maximum = size.waistStretchedCm;
      waistDistance = targetWaist < relaxed ? relaxed - targetWaist : targetWaist > maximum ? targetWaist - maximum : 0;
    } else {
      waistDistance = Math.abs(size.waistCm - targetWaist);
    }

    const hipDistance = Math.abs(size.hipCm - targetHip);
    const score = waistDistance * 2.4 + hipDistance * 1.15 + waistDeficit * 40 + hipDeficit * 30;

    return {
      size,
      score,
      viable: waistDeficit === 0 && hipDeficit === 0,
      calculations: { targetWaist, waistEase, waistMaximum, waistRoom, waistDeficit, targetHip, hipEase, hipRoom, hipDeficit },
    };
  }

  function scoreTopSize(size, body, config) {
    const tolerance = config.toleranceCm;
    const preference = body.fitPreference;
    const baseRoom = TOP_CHEST_ROOM[config.intendedFit] || TOP_CHEST_ROOM.regular;
    const shift = TOP_FIT_SHIFT[preference] ?? 0;
    const minimumRoom = Math.max(0, baseRoom[0] + shift);
    const maximumRoom = Math.max(minimumRoom, baseRoom[1] + shift);
    const targetRoom = minimumRoom + (maximumRoom - minimumRoom) * 0.35;
    const chestRoom = size.chestCm - body.chestCm;
    const chestDeficit = Math.max(0, minimumRoom - chestRoom - tolerance);
    const chestExcess = Math.max(0, chestRoom - maximumRoom - tolerance);
    const shoulderDifference = Number.isFinite(body.shoulderCm) && Number.isFinite(size.shoulderCm)
      ? Math.abs(size.shoulderCm - body.shoulderCm)
      : null;
    const shoulderWeight = config.intendedFit === 'oversized' ? 0.05 : 0.12;
    const score = Math.abs(chestRoom - targetRoom) + chestDeficit * 35 + chestExcess * 0.7 + (shoulderDifference ?? 0) * shoulderWeight;

    return {
      size,
      score,
      viable: chestDeficit === 0,
      calculations: { minimumRoom, maximumRoom, targetRoom, chestRoom, chestDeficit, chestExcess, shoulderDifference },
    };
  }

  const getConfidence = (body, config) => {
    if (config.profileType === 'bottom') {
      if (body.sources.waist === 'exact' && body.sources.hip === 'exact') return 'high';
      if (body.sources.waist === 'exact' || body.sources.hip === 'exact') return 'medium';
      return 'low';
    }
    if (body.sources.chest === 'exact' && body.sources.shoulder === 'exact') return 'high';
    if (body.sources.chest === 'exact') return 'medium';
    return 'low';
  };

  function recommendSize(rawConfig, input) {
    const validation = rawConfig && rawConfig.sizes ? { valid: true, config: rawConfig, errors: [] } : validateConfig(rawConfig);
    if (!validation.valid) return { valid: false, errors: validation.errors };

    const config = validation.config;
    const body = buildBodyProfile(input, config);
    if (config.profileType === 'bottom' && !Number.isFinite(body.waistCm)) {
      return { valid: true, matched: false, needsMeasurement: 'profile' };
    }
    if (config.profileType === 'bottom' && !Number.isFinite(body.hipCm)) {
      return { valid: true, matched: false, needsMeasurement: 'profile' };
    }
    if (config.profileType === 'top' && !Number.isFinite(body.chestCm)) {
      return { valid: true, matched: false, needsMeasurement: 'profile' };
    }

    const scored = config.sizes
      .map((size) => config.profileType === 'bottom' ? scoreBottomSize(size, body, config) : scoreTopSize(size, body, config))
      .sort((left, right) => left.score - right.score || left.size.index - right.size.index);
    const viable = scored.filter((candidate) => candidate.viable);

    if (!viable.length) {
      if (body.fitPreference !== 'intended') {
        const intendedBody = { ...body, fitPreference: 'intended' };
        const intendedScored = config.sizes
          .map((size) => config.profileType === 'bottom'
            ? scoreBottomSize(size, intendedBody, config)
            : scoreTopSize(size, intendedBody, config))
          .sort((left, right) => left.size.index - right.size.index);
        const intendedViable = intendedScored.filter((candidate) => candidate.viable);

        if (intendedViable.length) {
          const recommended = body.fitPreference === 'looser'
            ? intendedViable[intendedViable.length - 1]
            : intendedViable[0];

          return {
            valid: true,
            matched: true,
            body,
            confidence: getConfidence(body, config),
            recommended,
            alternative: null,
            boundaryFallback: body.fitPreference === 'looser' ? 'roomiest' : 'slimmest',
          };
        }
      }

      return { valid: true, matched: false, body, confidence: getConfidence(body, config), closest: scored[0] || null };
    }

    let recommended = viable[0];
    const next = viable[1] || null;
    if (next && Math.abs(next.score - recommended.score) <= 1.25) {
      if (body.fitPreference === 'looser' && next.size.index > recommended.size.index) recommended = next;
      if (body.fitPreference === 'closer' && next.size.index < recommended.size.index) recommended = next;
    }

    const alternative = viable.find((candidate) =>
      candidate !== recommended && Math.abs(candidate.score - recommended.score) <= 8
    ) || null;

    return { valid: true, matched: true, body, confidence: getConfidence(body, config), recommended, alternative };
  }

  const toCentimetres = (value, unit) => (unit === 'imperial' ? value * CM_PER_INCH : value);
  const fromCentimetres = (value, unit) => (unit === 'imperial' ? value / CM_PER_INCH : value);
  const toKilograms = (value, unit) => (unit === 'imperial' ? value * KG_PER_POUND : value);
  const fromKilograms = (value, unit) => (unit === 'imperial' ? value / KG_PER_POUND : value);

  const formatLength = (centimetres, unit) => {
    if (!Number.isFinite(centimetres)) return '—';
    return unit === 'imperial'
      ? `${round(centimetres / CM_PER_INCH, 1)} in`
      : `${round(centimetres, 1)} cm`;
  };

  const confidenceText = {
    high: 'High confidence — exact body measurements matched to this garment chart',
    medium: 'Good match — partly based on your exact measurements',
    low: 'Estimated fit — add body measurements for more accuracy',
  };

  const BaseElement = typeof HTMLElement === 'undefined' ? class {} : HTMLElement;

  class SizeRecommender extends BaseElement {
    connectedCallback() {
      if (this.abortController) this.abortController.abort();
      this.abortController = new AbortController();
      this.form = this.querySelector('[data-size-recommender-form]');
      this.dataScript = this.querySelector('[data-size-recommender-data]');

      if (!this.form || !this.dataScript) return;

      try {
        this.rawConfig = JSON.parse(this.dataScript.textContent);
      } catch (error) {
        this.setUnavailable(['The product sizing data is not valid JSON.']);
        return;
      }

      const validation = validateConfig(this.rawConfig);
      if (!validation.valid) {
        this.setUnavailable(validation.errors);
        return;
      }

      this.config = validation.config;
      this.steps = Array.from(this.querySelectorAll('[data-size-step]'));
      this.resultPanel = this.querySelector('[data-size-result]');
      this.formPanel = this.querySelector('[data-size-form-panel]');
      this.error = this.querySelector('[data-size-error]');
      this.progressText = this.querySelector('[data-size-progress-text]');
      this.progressFill = this.querySelector('[data-size-progress-fill]');
      this.progressDots = Array.from(this.querySelectorAll('[data-size-progress-dot]'));
      this.progressContainer = this.querySelector('.size-recommender__flow-progress');
      this.currentStep = 0;
      this.unit = this.form.elements.unit?.value || 'metric';

      this.restoreProfile();
      this.updateUnitPresentation(false);
      if (this.hasRequiredMeasurement()) {
        this.calculate({ automatic: true });
      } else {
        this.showStep(0, false);
      }

      // The drawer lives inside the theme's <variant-picker>. Keep calculator
      // inputs from reaching the variant picker's delegated listeners, or a
      // body-profile radio can be mistaken for a product option and rerender
      // the whole product block.
      this.addEventListener('click', (event) => {
        event.stopPropagation();
        this.onClick(event);
      }, {
        signal: this.abortController.signal,
      });
      this.addEventListener('input', (event) => {
        event.stopPropagation();
        this.clearError();
      }, {
        signal: this.abortController.signal,
      });
      this.addEventListener('change', (event) => {
        event.stopPropagation();
        this.onChange(event);
      }, {
        signal: this.abortController.signal,
      });
      this.form.addEventListener(
        'submit',
        (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.calculate();
        },
        { signal: this.abortController.signal }
      );
    }

    disconnectedCallback() {
      this.abortController?.abort();
    }

    setUnavailable(errors) {
      this.setAttribute('hidden', '');
      const drawer = this.closest('x-drawer');
      if (drawer?.id && typeof document !== 'undefined') {
        const trigger = Array.from(document.querySelectorAll('[aria-controls]')).find(
          (element) => element.getAttribute('aria-controls') === drawer.id
        );
        trigger?.setAttribute('hidden', '');
        drawer.setAttribute('hidden', '');
      }
      if (typeof console !== 'undefined') {
        console.warn('[Distopion size recommender] Hidden because sizing QA failed:', errors);
      }
    }

    onClick(event) {
      const resultFitButton = event.target.closest('[data-size-result-fit]');
      const resultFit = resultFitButton?.dataset.sizeResultFit;
      if (resultFit) {
        this.setResultFit(resultFit);
        return;
      }

      const action = event.target.closest('[data-size-action]')?.dataset.sizeAction;
      if (!action) return;

      if (action === 'next') {
        if (this.validateStep(this.currentStep)) this.showStep(this.currentStep + 1);
      } else if (action === 'back') {
        this.showStep(this.currentStep - 1);
      } else if (action === 'restart') {
        this.restart();
      } else if (action === 'select') {
        this.selectRecommendedSize();
      }
    }

    onChange(event) {
      if (event.target.name !== 'unit') return;
      const nextUnit = event.target.value;
      if (nextUnit === this.unit) return;
      this.convertEnteredValues(this.unit, nextUnit);
      this.unit = nextUnit;
      this.updateUnitPresentation(false);
    }

    showStep(index, moveFocus = true) {
      this.currentStep = clamp(index, 0, this.steps.length - 1);
      this.steps.forEach((step, stepIndex) => {
        step.hidden = stepIndex !== this.currentStep;
      });
      this.progressDots.forEach((dot, dotIndex) => {
        dot.classList.toggle('is-active', dotIndex === this.currentStep);
        dot.setAttribute('aria-current', dotIndex === this.currentStep ? 'step' : 'false');
      });
      this.formPanel.hidden = false;
      this.resultPanel.hidden = true;
      if (this.progressContainer) this.progressContainer.hidden = false;
      if (this.progressText) this.progressText.textContent = `Step ${this.currentStep + 1} of ${this.steps.length}`;
      if (this.progressFill) this.progressFill.style.width = `${((this.currentStep + 1) / this.steps.length) * 100}%`;
      this.clearError();

      if (moveFocus) {
        const heading = this.steps[this.currentStep]?.querySelector('[data-size-step-heading]');
        heading?.focus({ preventScroll: true });
      }
    }

    hasRequiredMeasurement() {
      const exactBottom =
        finiteNumber(this.form.elements.body_waist?.value) !== null &&
        finiteNumber(this.form.elements.body_hip?.value) !== null;
      const exactTop = finiteNumber(this.form.elements.body_chest?.value) !== null;
      const visualProfile =
        finiteNumber(this.form.elements.height?.value) !== null &&
        finiteNumber(this.form.elements.weight?.value) !== null &&
        Boolean(this.form.elements.stomach_shape?.value) &&
        Boolean(this.form.elements.torso_shape?.value) &&
        Boolean(this.form.elements.thigh_shape?.value);
      return this.config.profileType === 'bottom' ? exactBottom || visualProfile : exactTop || visualProfile;
    }

    validateStep(stepIndex = this.currentStep) {
      if (stepIndex === 0) {
        const heightInput = this.form.elements.height;
        const heightValue = finiteNumber(heightInput?.value);
        const heightCm = heightValue === null ? null : toCentimetres(heightValue, this.unit);
        if (heightCm === null || heightCm < 130 || heightCm > 220) {
          this.showError('Enter your height between 130 and 220 cm.');
          heightInput?.focus();
          return false;
        }

        const weightInput = this.form.elements.weight;
        const weightValue = finiteNumber(weightInput?.value);
        const weightKg = weightValue === null ? null : toKilograms(weightValue, this.unit);
        if (weightKg === null || weightKg < 35 || weightKg > 180) {
          this.showError('Enter your weight between 35 and 180 kg.');
          weightInput?.focus();
          return false;
        }

        const ageInput = this.form.elements.age;
        const age = finiteNumber(ageInput?.value);
        if (age !== null && (age < 16 || age > 100)) {
          this.showError('Enter an age between 16 and 100, or leave it blank.');
          ageInput?.focus();
          return false;
        }
        return true;
      }

      const shapeSteps = {
        1: ['stomach_shape', 'stomach shape'],
        2: ['torso_shape', 'torso shape'],
        3: ['thigh_shape', 'thigh shape'],
      };
      if (shapeSteps[stepIndex]) {
        const [name, label] = shapeSteps[stepIndex];
        if (!this.form.elements[name]?.value) {
          this.showError(`Choose the ${label} that is closest to yours.`);
          this.form.querySelector(`[name="${name}"]`)?.focus();
          return false;
        }
        return true;
      }

      const optionalMeasurements = this.config.profileType === 'bottom'
        ? [['body_waist', 'body waist', 45, 180], ['body_hip', 'body hips', 55, 220]]
        : [['body_chest', 'body chest', 60, 200], ['body_shoulder', 'shoulder width', 25, 80]];

      for (const [name, label, minimum, maximum] of optionalMeasurements) {
        const input = this.form.elements[name];
        const value = finiteNumber(input?.value);
        if (value === null) continue;
        const centimetres = toCentimetres(value, this.unit);
        if (centimetres < minimum || centimetres > maximum) {
          this.showError(`Check your ${label}; it should be between ${minimum} and ${maximum} cm.`);
          input?.focus();
          return false;
        }
      }

      if (!this.form.elements.fit_preference?.value) {
        this.showError('Choose how you want this item to fit.');
        this.form.querySelector('[name="fit_preference"]')?.focus();
        return false;
      }
      return true;
    }

    collectInput() {
      const length = (name) => {
        const value = finiteNumber(this.form.elements[name]?.value);
        return value === null ? null : toCentimetres(value, this.unit);
      };
      const weight = finiteNumber(this.form.elements.weight?.value);

      return {
        heightCm: length('height'),
        weightKg: weight === null ? null : toKilograms(weight, this.unit),
        age: finiteNumber(this.form.elements.age?.value),
        stomachShape: this.form.elements.stomach_shape?.value || '',
        torsoShape: this.form.elements.torso_shape?.value || '',
        thighShape: this.form.elements.thigh_shape?.value || '',
        bodyWaistCm: length('body_waist'),
        bodyHipCm: length('body_hip'),
        bodyChestCm: length('body_chest'),
        bodyShoulderCm: length('body_shoulder'),
        fitPreference: this.form.elements.fit_preference?.value || 'intended',
      };
    }

    calculate({ automatic = false } = {}) {
      if (!this.hasRequiredMeasurement()) {
        this.updateTriggerRecommendation(null);
        if (!automatic) {
          this.showStep(0, false);
          this.validateStep(0);
        }
        return;
      }
      if (!automatic) {
        for (let index = 0; index < this.steps.length; index += 1) {
          if (!this.validateStep(index)) {
            this.showStep(index, false);
            return;
          }
        }
      }
      const input = this.collectInput();
      const result = recommendSize(this.config, input);

      if (!result.valid) {
        this.showError('This product’s sizing data needs review. Please use the size chart.');
        return;
      }

      if (result.needsMeasurement) {
        this.updateTriggerRecommendation(null);
        if (!automatic) {
          this.showStep(0, false);
          this.validateStep(0);
        }
        return;
      }

      this.lastResult = result;
      if (!automatic) this.saveProfile(input);
      this.renderResult(result, { moveFocus: !automatic });
    }

    renderResult(result, { moveFocus = true } = {}) {
      this.formPanel.hidden = true;
      this.resultPanel.hidden = false;
      if (this.progressContainer) this.progressContainer.hidden = true;
      if (this.progressText) this.progressText.textContent = 'Your result';
      if (this.progressFill) this.progressFill.style.width = '100%';

      const match = this.querySelector('[data-size-match]');
      const noMatch = this.querySelector('[data-size-no-match]');
      const preference = result.body?.fitPreference || this.form.elements.fit_preference?.value || 'intended';

      this.querySelectorAll('[data-size-result-fit]').forEach((button) => {
        const active = button.dataset.sizeResultFit === preference;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', active ? 'true' : 'false');
      });

      if (!result.matched) {
        this.updateTriggerRecommendation(null);
        match.hidden = true;
        noMatch.hidden = false;
        const closest = result.closest?.size?.label;
        const copy = noMatch.querySelector('[data-size-no-match-copy]');
        copy.textContent = closest
          ? `None of the available sizes gives a reliable ${this.fitPreferenceLabel(preference).toLowerCase()} for these measurements. ${closest} is closest, but we will not guess.`
          : 'This product does not have a reliable match for the measurements entered.';
        if (moveFocus) noMatch.querySelector('h2')?.focus({ preventScroll: true });
        return;
      }

      match.hidden = false;
      noMatch.hidden = true;
      const label = result.recommended.size.label;
      this.updateTriggerRecommendation(label);
      this.querySelector('[data-size-result-label]').textContent = label;
      const fitStatus = this.querySelector('[data-size-fit-status]');
      fitStatus.textContent = result.boundaryFallback === 'roomiest'
        ? 'Roomiest available'
        : result.boundaryFallback === 'slimmest'
          ? 'Slimmest available'
          : preference === 'intended'
            ? 'Best match'
            : this.fitPreferenceLabel(preference);

      const confidence = this.querySelector('[data-size-confidence]');
      confidence.dataset.confidence = result.confidence;
      confidence.textContent = result.boundaryFallback
        ? 'Closest option in this product’s size range'
        : confidenceText[result.confidence];
      this.querySelector('[data-size-result-copy]').textContent = this.resultCopy(result);

      const alternative = this.querySelector('[data-size-alternative]');
      alternative.hidden = true;

      this.renderMath(result);
      const selectButton = this.querySelector('[data-select-size]');
      const optionValue = this.findOptionValue(result.recommended.size);
      selectButton.hidden = !optionValue;
      selectButton.disabled = false;
      selectButton.textContent = optionValue?.available === false ? `View size ${label}` : `Select size ${label}`;

      if (moveFocus) this.querySelector('[data-size-result-heading]')?.focus({ preventScroll: true });
      this.dispatchEvent(
        new CustomEvent('distopion:size-recommendation', {
          bubbles: true,
          detail: {
            productHandle: this.config.productHandle,
            size: label,
            confidence: result.confidence,
            fitPreference: preference,
          },
        })
      );
    }

    fitPreferenceLabel(preference) {
      return {
        closer: 'Slimmer fit',
        intended: 'Ideal fit',
        looser: 'Looser fit',
      }[preference] || 'Ideal fit';
    }

    resultCopy(result) {
      const label = result.recommended.size.label;
      const preference = result.body.fitPreference;
      const primarySource = this.config.profileType === 'bottom' ? result.body.sources.waist : result.body.sources.chest;
      const basis = primarySource === 'estimated'
        ? 'your height, weight and body-shape profile'
        : 'your body measurements';

      if (result.boundaryFallback === 'roomiest') {
        return `Size ${label} is the roomiest option available for this product. It will fit closer than your selected preference.`;
      }
      if (result.boundaryFallback === 'slimmest') {
        return `Size ${label} is the slimmest option available for this product. It will fit roomier than your selected preference.`;
      }
      if (preference === 'closer') {
        return `Based on ${basis}, size ${label} gives the closest fit this product can reliably support.`;
      }
      if (preference === 'looser') {
        return `Based on ${basis}, choose size ${label} for a roomier version of this product’s intended shape.`;
      }
      return `Based on ${basis}, size ${label} gives the fit this product was designed for.`;
    }

    renderMath(result) {
      const container = this.querySelector('[data-size-math]');
      container.replaceChildren();
      const body = result.body;
      const size = result.recommended.size;
      const rows = [];
      const sourceNote = (source) => source === 'estimated' ? ' (estimated)' : '';

      if (this.config.profileType === 'bottom') {
        rows.push(['Body waist', `${formatLength(body.waistCm, this.unit)}${sourceNote(body.sources.waist)}`]);
        rows.push(['Garment waist', formatLength(size.waistCm, this.unit)]);
        if (this.config.elasticWaist) rows.push(['Maximum stretched waist', formatLength(size.waistStretchedCm, this.unit)]);
        rows.push(['Hip room', `${formatLength(result.recommended.calculations.hipRoom, this.unit)}${sourceNote(body.sources.hip)}`]);
        if (size.outseamCm) rows.push(['Garment length', formatLength(size.outseamCm, this.unit)]);
      } else {
        rows.push(['Body chest', `${formatLength(body.chestCm, this.unit)}${sourceNote(body.sources.chest)}`]);
        rows.push(['Chest room', formatLength(result.recommended.calculations.chestRoom, this.unit)]);
        if (Number.isFinite(body.shoulderCm) && size.shoulderCm) {
          rows.push(['Shoulder', `${formatLength(body.shoulderCm, this.unit)} body${sourceNote(body.sources.shoulder)} · ${formatLength(size.shoulderCm, this.unit)} garment`]);
        }
        if (size.garmentLengthCm) rows.push(['Garment length', formatLength(size.garmentLengthCm, this.unit)]);
      }

      rows.push(['Designed fit', titleCase(this.config.intendedFit)]);
      rows.push(['Measurement tolerance', `±${formatLength(this.config.toleranceCm, this.unit)}`]);

      rows.forEach(([label, value]) => {
        const row = document.createElement('div');
        row.className = 'size-recommender__math-row';
        const labelElement = document.createElement('span');
        labelElement.className = 'size-recommender__math-label';
        labelElement.textContent = label;
        const valueElement = document.createElement('span');
        valueElement.className = 'size-recommender__math-value';
        valueElement.textContent = value;
        row.append(labelElement, valueElement);
        container.append(row);
      });
    }

    findOptionValue(sizeOrLabel) {
      if (sizeOrLabel && typeof sizeOrLabel === 'object') {
        const indexed = this.config.optionValues[sizeOrLabel.index];
        if (indexed) return indexed;
        if (sizeOrLabel.optionValue) return sizeOrLabel.optionValue;
      }
      const label = typeof sizeOrLabel === 'object' ? sizeOrLabel.label : sizeOrLabel;
      const normalized = normalizeLabel(label);
      const row = this.config.sizes.find((size) =>
        normalizeLabel(size.label) === normalized || normalizeLabel(size.supplierLabel) === normalized
      );
      return (row ? this.config.optionValues[row.index] : null) ||
        this.config.optionValues.find((option) => normalizeLabel(option.label) === normalized) || null;
    }

    updateTriggerRecommendation(label) {
      if (typeof document === 'undefined') return;
      const drawer = this.closest('x-drawer');
      if (!drawer?.id) return;
      const trigger = Array.from(document.querySelectorAll('[aria-controls]')).find(
        (element) => element.getAttribute('aria-controls') === drawer.id
      );
      if (!trigger) return;

      const defaultLabel = trigger.dataset.sizeRecommenderDefault || 'Find your size';
      if (label) {
        trigger.textContent = `Recommended size: ${label}`;
        trigger.dataset.recommendedSize = label;
        trigger.setAttribute('aria-label', `Recommended size ${label}. Open size details.`);
        trigger.classList.add('is-recommended');
      } else {
        trigger.textContent = defaultLabel;
        delete trigger.dataset.recommendedSize;
        trigger.removeAttribute('aria-label');
        trigger.classList.remove('is-recommended');
      }
    }

    selectRecommendedSize() {
      const recommendedSize = this.lastResult?.recommended?.size;
      const label = recommendedSize?.label;
      if (!label || typeof document === 'undefined') return;
      const optionValue = this.findOptionValue(recommendedSize);
      if (!optionValue) return;

      const pickers = Array.from(document.querySelectorAll('variant-picker'));
      const picker = pickers.find(
        (candidate) => !this.config.sectionId || candidate.getAttribute('section-id') === this.config.sectionId
      ) || pickers[0];
      const inputs = picker
        ? Array.from(picker.querySelectorAll('input[data-option-position]'))
        : [];
      const input = inputs.find((candidate) => {
        const samePosition =
          this.config.optionPosition === null ||
          Number(candidate.dataset.optionPosition) === Number(this.config.optionPosition);
        return samePosition && String(candidate.value) === String(optionValue.id);
      });
      const button = this.querySelector('[data-select-size]');

      if (!input || input.disabled) {
        button.textContent = `Size ${label} is unavailable`;
        button.disabled = true;
        return;
      }

      input.click();
      button.textContent = `Size ${label} selected`;
      button.disabled = true;
    }

    setResultFit(preference) {
      if (!['closer', 'intended', 'looser'].includes(preference)) return;
      const input = this.form.querySelector(`[name="fit_preference"][value="${preference}"]`);
      if (!input) return;
      input.checked = true;
      this.calculate();
    }

    restart() {
      this.lastResult = null;
      this.form.reset();
      this.unit = 'metric';
      this.updateUnitPresentation(false);
      if (typeof localStorage !== 'undefined') {
        try {
          localStorage.removeItem(STORAGE_KEY);
          localStorage.removeItem(LEGACY_STORAGE_KEY);
        } catch (error) {
          // Storage can be unavailable; the form can still restart.
        }
      }
      this.updateTriggerRecommendation(null);
      this.showStep(0);
    }

    showError(message) {
      this.error.textContent = message;
      this.error.hidden = false;
    }

    clearError() {
      if (!this.error) return;
      this.error.hidden = true;
      this.error.textContent = '';
    }

    convertEnteredValues(fromUnit, toUnit) {
      this.form.querySelectorAll('[data-unit-kind]').forEach((input) => {
        const value = finiteNumber(input.value);
        if (value === null) return;
        if (input.dataset.unitKind === 'weight') {
          const kilograms = toKilograms(value, fromUnit);
          input.value = round(fromKilograms(kilograms, toUnit), toUnit === 'imperial' ? 0 : 1);
        } else {
          const centimetres = toCentimetres(value, fromUnit);
          input.value = round(fromCentimetres(centimetres, toUnit), 1);
        }
      });
    }

    updateUnitPresentation() {
      const imperial = this.unit === 'imperial';
      this.querySelectorAll('[data-unit-suffix]').forEach((element) => {
        element.textContent = element.dataset.unitKind === 'weight'
          ? imperial ? 'lb' : 'kg'
          : imperial ? 'in' : 'cm';
      });

      this.form.querySelectorAll('[data-unit-kind]').forEach((input) => {
        const kind = input.dataset.unitKind;
        if (kind === 'weight') {
          input.min = imperial ? '77' : '35';
          input.max = imperial ? '397' : '180';
          input.step = imperial ? '1' : '0.1';
        } else if (input.name === 'height') {
          input.min = imperial ? '55.1' : '140';
          input.max = imperial ? '84.6' : '215';
          input.step = '0.1';
        } else {
          input.step = '0.1';
        }
      });
    }

    readStoredProfile() {
      if (typeof localStorage === 'undefined') return null;
      try {
        const current = JSON.parse(localStorage.getItem(STORAGE_KEY));
        if (current?.version === VERSION) return current;

        const legacy = JSON.parse(localStorage.getItem(LEGACY_STORAGE_KEY));
        return legacy?.version === 2 ? legacy : null;
      } catch (error) {
        return null;
      }
    }

    saveProfile(input) {
      if (typeof localStorage === 'undefined') return;
      try {
        const saved = this.readStoredProfile() || {};
        const profile = {
          ...saved,
          version: VERSION,
          unit: this.unit,
          fitPreference: input.fitPreference || saved.fitPreference || 'intended',
          updatedAt: new Date().toISOString(),
        };
        const reusableMeasurements = [
          'heightCm',
          'weightKg',
          'age',
          'inseamCm',
          'bodyWaistCm',
          'bodyHipCm',
          'bodyChestCm',
          'bodyShoulderCm',
        ];

        reusableMeasurements.forEach((key) => {
          if (Number.isFinite(input[key])) profile[key] = input[key];
        });
        ['stomachShape', 'torsoShape', 'thighShape'].forEach((key) => {
          if (input[key]) profile[key] = input[key];
        });

        localStorage.setItem(STORAGE_KEY, JSON.stringify(profile));
        localStorage.removeItem(LEGACY_STORAGE_KEY);
      } catch (error) {
        // Storage can be unavailable in private contexts; the calculator still works.
      }
    }

    restoreProfile() {
      const saved = this.readStoredProfile();
      if (!saved) return;
      const unit = saved.unit === 'imperial' ? 'imperial' : 'metric';
      const unitInput = this.form.querySelector(`[name="unit"][value="${unit}"]`);
      if (unitInput) unitInput.checked = true;
      this.unit = unit;

      const setLength = (name, value) => {
        const input = this.form.elements[name];
        if (input && Number.isFinite(value)) input.value = round(fromCentimetres(value, unit), 1);
      };
      const setValue = (name, value) => {
        const input = this.form.elements[name];
        if (input && value !== null && value !== undefined) input.value = value;
      };

      setLength('height', saved.heightCm);
      if (this.form.elements.weight && Number.isFinite(saved.weightKg)) {
        this.form.elements.weight.value = round(fromKilograms(saved.weightKg, unit), unit === 'imperial' ? 0 : 1);
      }
      setValue('age', saved.age);
      ['stomachShape', 'torsoShape', 'thighShape'].forEach((key) => {
        const inputName = key.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
        const choice = this.form.querySelector(`[name="${inputName}"][value="${saved[key]}"]`);
        if (choice) choice.checked = true;
      });
      setValue('usual_size', saved.usualSize || '');
      setLength('usual_waist', saved.usualWaistCm);
      setLength('inseam', saved.inseamCm);
      setLength('body_waist', saved.bodyWaistCm);
      setLength('body_hip', saved.bodyHipCm);
      setLength('body_chest', saved.bodyChestCm);
      setLength('body_shoulder', saved.bodyShoulderCm);

      const bodyProfile = this.form.querySelector(`[name="body_profile"][value="${saved.bodyProfile}"]`);
      if (bodyProfile) bodyProfile.checked = true;
      const fitPreference = this.form.querySelector(`[name="fit_preference"][value="${saved.fitPreference}"]`);
      if (fitPreference) fitPreference.checked = true;
    }
  }

  const api = Object.freeze({
    validateConfig,
    recommendSize,
    buildBodyProfile,
    normalizeLabel,
  });

  if (typeof window !== 'undefined') window.DistopionSizeEngine = api;
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (typeof customElements !== 'undefined' && !customElements.get('size-recommender')) {
    customElements.define('size-recommender', SizeRecommender);
  }
})();
